<script lang="ts">
import {defineComponent} from 'vue'
import SearchApi from '@/api/SearchApi'

type SuggestionItem = {
  key: string
  html: string
  value: string
}

export default defineComponent({
  name: 'SearchAutocompleteInput',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    buttonLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'search'],
  data() {
    return {
      apiSuggestions: [] as string[],
      isOpen: false,
      requestId: 0,
      activeIndex: -1,
      baseInputValue: '',
      rootEl: null as null | HTMLElement,
    }
  },
  computed: {
    localValue: {
      get(): string {
        return this.modelValue
      },
      set(value: string) {
        this.$emit('update:modelValue', value)
      },
    },
    showSuggestions(): boolean {
      return this.isOpen && this.localValue.trim().length >= 2
    },
    suggestions(): SuggestionItem[] {
      const value = this.localValue.trim()
      if (value.length < 2) {
        return []
      }

      const normalizedValue = value.toLowerCase()
      return this.apiSuggestions
        .map((item, index) => ({
          key: `api-${index}`,
          html: this.transformHighlights(item),
          value: this.stripHtml(item),
        }))
        .filter((item) => item.value.length > 0 && item.value.toLowerCase() !== normalizedValue)
        .slice(0, 10)
    },
  },
  methods: {
    handleInput(event: Event) {
      const value = (event.target as HTMLInputElement).value
      this.$emit('update:modelValue', value)
      this.isOpen = value.trim().length >= 2
      this.activeIndex = -1
      this.baseInputValue = value
      this.fetchAutocomplete(value)
    },
    handleFocus() {
      this.isOpen = this.localValue.trim().length >= 2
      if (this.isOpen) {
        this.fetchAutocomplete(this.localValue)
      }
    },
    handleDocumentMouseDown(event: MouseEvent) {
      if (event.button !== 0) {
        return
      }
      if (!this.rootEl) {
        return
      }
      if (!this.rootEl.contains(event.target as Node)) {
        this.isOpen = false
        this.activeIndex = -1
      }
    },
    handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        if (this.activeIndex >= 0 && this.suggestions[this.activeIndex]) {
          event.preventDefault()
          this.selectSuggestion(this.suggestions[this.activeIndex].value)
        } else {
          this.handleSearch()
        }
        return
      }

      if (!this.showSuggestions || this.suggestions.length === 0) {
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (this.activeIndex === -1) {
          this.baseInputValue = this.localValue
        }
        this.activeIndex = Math.min(this.activeIndex + 1, this.suggestions.length - 1)
        this.$emit('update:modelValue', this.suggestions[this.activeIndex].value)
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (this.activeIndex > 0) {
          this.activeIndex -= 1
          this.$emit('update:modelValue', this.suggestions[this.activeIndex].value)
          return
        }
        if (this.activeIndex === 0) {
          this.activeIndex = -1
          this.$emit('update:modelValue', this.baseInputValue)
        }
      }
    },
    handleSearch() {
      this.isOpen = false
      this.$emit('search', this.localValue)
    },
    selectSuggestion(value: string) {
      this.$emit('update:modelValue', value)
      this.isOpen = false
      this.activeIndex = -1
      this.$emit('search', value)
    },
    fetchAutocomplete(query: string) {
      const trimmed = query.trim()
      if (trimmed.length < 2) {
        this.apiSuggestions = []
        this.activeIndex = -1
        return
      }

      const currentRequest = ++this.requestId
      SearchApi.getSearchAutocompleteResults(trimmed)
        .then((resp) => {
          if (currentRequest !== this.requestId) {
            return
          }
          const hits = Array.isArray(resp?.data?.hits) ? resp.data.hits : []
          this.apiSuggestions = hits.map((item: string) => String(item))
          this.activeIndex = -1
        })
        .catch(() => {
          if (currentRequest !== this.requestId) {
            return
          }
          this.apiSuggestions = []
          this.activeIndex = -1
        })
    },
    stripHtml(value: string) {
      return value.replace(/<[^>]*>/g, '').trim()
    },
    transformHighlights(value: string) {
      return value
        .replace(/<mark>/g, '<span class="font-semibold">')
        .replace(/<\/mark>/g, '</span>')
    },
  },
  mounted() {
    this.rootEl = this.$refs.root as HTMLElement
    document.addEventListener('mousedown', this.handleDocumentMouseDown)
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.handleDocumentMouseDown)
  },
})
</script>

<template>
  <div ref="root" class="relative w-full">
    <input
      v-model="localValue"
      @input="handleInput"
      @focus="handleFocus"
      @keydown="handleKeydown"
      class="w-full rounded-full border border-gray-300 py-3 px-6 focus:outline-none focus:shadow-md placeholder:text-transparent sm:placeholder:text-gray-400"
      type="text"
      name="search"
      :placeholder="placeholder"
      maxlength="1024"
      autocomplete="off"
    />

    <button
      class="absolute right-1 top-1 text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 font-medium rounded-full text-sm px-5 py-2.5 text-center cursor-pointer"
      type="button"
      @click="handleSearch"
    >
      {{ buttonLabel }}
    </button>

    <div
      v-if="showSuggestions && suggestions.length > 0"
      class="absolute left-0 right-0 mt-2 rounded-2xl border border-gray-200 bg-white shadow-lg z-20 overflow-hidden"
    >
      <div class="max-h-80 overflow-auto py-1">
        <button
          v-for="(item, index) in suggestions"
          :key="item.key"
          type="button"
          class="w-full px-5 py-2 text-left hover:bg-gray-100 cursor-pointer"
          :class="[
            'text-gray-700',
            index === activeIndex ? 'bg-gray-100' : ''
          ]"
          @click="selectSuggestion(item.value)"
        >
          <span v-html="item.html"></span>
        </button>
      </div>
    </div>
  </div>
</template>
